import { useEffect, useState, type ReactNode } from 'react'
import { api } from '../../lib/api'
import { explain } from '../../lib/staffErrors'
import type { EquipmentProfileRow, OperationProfileRow } from '../../lib/types'
import { EmptyState } from '../../ui/parts'
import { useStaffText } from '../../ui/StaffText'
import { Field, Text } from '../parts'
import { IconTile, ListCard, ListRow, ListTable, NameCell, Notice, RowArrow, toneOf } from '../settingsKit'
import { SetupModal } from '../setupKit'
import '../../styles/equipment.css'

/** An operation profile's equipment profiles as a list; a row opens that profile's page. */
export function EquipmentList({
  operationProfileId,
  profiles,
  onProfile,
  onOpen,
  foot,
}: {
  operationProfileId: string
  /** With several operation profiles, chips switch between them. */
  profiles?: OperationProfileRow[]
  onProfile?: (id: string) => void
  onOpen: (id: string) => void
  foot?: ReactNode
}) {
  const { lang, say } = useStaffText()
  const [rows, setRows] = useState<EquipmentProfileRow[] | null>(null)
  const [error, setError] = useState('')
  const [find, setFind] = useState('')

  useEffect(() => {
    let alive = true
    setRows(null)
    setError('')
    api
      .equipmentProfiles(operationProfileId)
      .then((r) => alive && setRows(r.equipmentProfiles))
      .catch((err) => {
        if (!alive) return
        setError(explain(err, lang, 'common.unreachable'))
        setRows([])
      })
    return () => {
      alive = false
    }
  }, [operationProfileId, lang])

  const q = find.trim().toLocaleLowerCase()
  const shown = (rows ?? []).filter((r) => !q || `${r.name} ${r.description}`.toLocaleLowerCase().includes(q))

  return (
    <ListCard
      title={say('eq.tab.profiles')}
      count={rows ? say('settings.equipment.count', { n: rows.length }) : undefined}
      search={find}
      onSearch={setFind}
      searchLabel={say('settings.equipment.find')}
      chips={profiles?.map((p) => ({ value: p.id, label: p.name }))}
      chip={operationProfileId}
      onChip={onProfile}
      foot={foot}
    >
      {error && <Notice className="eq-list__notice">{error}</Notice>}
      {!rows && <p className="empty">{say('common.loading')}</p>}
      {rows && rows.length > 0 && shown.length === 0 && <EmptyState title={say('admin.findNone', { q: find.trim() })} />}
      {shown.length > 0 && (
        <ListTable
          label={say('eq.tab.profiles')}
          cols="minmax(240px, 1.8fr) 140px 16px"
          head={[say('settings.equipment.col.profile'), say('settings.col.branches'), '']}
        >
          {shown.map((r) => (
            <ListRow key={r.id} onOpen={() => onOpen(r.id)}>
              <NameCell tile={<IconTile icon="machine" tone={toneOf(r.id)} />} name={r.name} sub={r.description || undefined} />
              <span role="cell" className="set-muted">
                {r.branchCount > 0 ? say('eq.tab.inUse', { n: r.branchCount }) : '—'}
              </span>
              <RowArrow />
            </ListRow>
          ))}
        </ListTable>
      )}
    </ListCard>
  )
}

/** Names a new equipment profile; its page opens once it exists. */
export function EquipmentAddSheet({
  operationProfileId,
  onClose,
  onMade,
}: {
  operationProfileId: string
  onClose: () => void
  onMade: (id: string) => void
}) {
  const { lang, say } = useStaffText()
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      const r = await api.createEquipmentProfile(operationProfileId, { name: name.trim() })
      onMade(r.equipmentProfile.id)
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SetupModal
      title={say('eq.tab.add')}
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel={say('common.save')}
      valid={name.trim() !== ''}
      busy={busy}
      error={error}
    >
      <Field label={say('common.name')}>
        <Text value={name} onChange={setName} required />
      </Field>
    </SetupModal>
  )
}
